import { Link } from "@ui/Atoms/Link/Link";
import { IconSearch } from "@ui/Icons/IconSearch";
import { DEFAULT_BRAND_LOGO } from "@ui/Molecules/Brand/defaultBrandLogo";
import { type ReactNode, useEffect, useState } from "react";
import type { TopBarLink, TopBarTranslations } from "./types";

type Props = {
	brandName: string;
	brandLogo: string;
	brandHref?: string;
	links: TopBarLink[];
	translations: TopBarTranslations;
	searchHref?: string;
	onSearchClick?: () => void;
	/** Submit handler for the live search field. Takes precedence over the link. */
	onSearch?: (query: string) => void;
	/** Prefill for the search field. */
	searchValue?: string;
	actions?: ReactNode;
};

export function TopBar({
	brandName,
	brandLogo,
	brandHref = "/",
	links,
	translations,
	searchHref,
	onSearchClick,
	onSearch,
	searchValue = "",
	actions,
}: Props) {
	const [query, setQuery] = useState(searchValue);
	useEffect(() => {
		setQuery(searchValue);
	}, [searchValue]);
	return (
		<header className="sticky top-0 z-[900] hidden h-[72px] items-center gap-8 border-b border-[var(--border-default)] bg-[var(--surface-default)] px-10 md:flex">
			<Link
				href={brandHref}
				className="flex shrink-0 items-center gap-2.5 text-[var(--content-primary)]"
			>
				<img
					src={brandLogo || DEFAULT_BRAND_LOGO}
					alt=""
					onError={(event) => {
						event.currentTarget.src = DEFAULT_BRAND_LOGO;
					}}
					className="h-8 w-8 rounded-[var(--radius-pill)] object-cover"
				/>
				<span
					className="text-[18px] font-extrabold tracking-[-0.02em]"
					style={{ fontFamily: "var(--font-display)" }}
				>
					{brandName}
				</span>
			</Link>

			<nav className="flex items-center gap-1">
				{links.map((link) => (
					<Link
						key={link.href}
						href={link.href}
						aria-current={link.active ? "page" : undefined}
						className="rounded-[var(--radius-sm)] px-3.5 py-2 text-[14px] font-bold"
						style={{
							color: link.active
								? "var(--content-primary)"
								: "var(--content-muted)",
							background: link.active
								? "var(--surface-elevated)"
								: "transparent",
						}}
					>
						{link.label}
						{link.badge !== undefined && link.badge !== "" && (
							<span className="ml-1 text-[var(--content-faint)]">
								{link.badge}
							</span>
						)}
					</Link>
				))}
			</nav>

			<div className="flex flex-1 items-center justify-end gap-3.5">
				{onSearch ? (
					<form
						onSubmit={(event) => {
							event.preventDefault();
							onSearch(query.trim());
						}}
						className="flex w-[300px] items-center gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-muted)] px-3.5 py-2.5 focus-within:border-[var(--accent-500)]"
					>
						<IconSearch size={16} color="var(--content-faint)" />
						<input
							type="search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={translations.searchPlaceholder}
							aria-label={translations.searchPlaceholder}
							className="flex-1 border-0 bg-transparent text-[14px] text-[var(--content-primary)] outline-none placeholder:text-[var(--content-faint)]"
						/>
					</form>
				) : onSearchClick ? (
					<button
						type="button"
						onClick={onSearchClick}
						className="flex w-[300px] items-center gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-muted)] px-3.5 py-2.5 text-left"
					>
						<IconSearch size={16} color="var(--content-faint)" />
						<span className="text-[14px] text-[var(--content-faint)]">
							{translations.searchPlaceholder}
						</span>
					</button>
				) : (
					<Link
						href={searchHref ?? "/zoeken"}
						className="flex w-[300px] items-center gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-muted)] px-3.5 py-2.5"
					>
						<IconSearch size={16} color="var(--content-faint)" />
						<span className="text-[14px] text-[var(--content-faint)]">
							{translations.searchPlaceholder}
						</span>
					</Link>
				)}

				{actions}
			</div>
		</header>
	);
}
